import * as pdfjsLib from 'pdfjs-dist';

// Set worker path for PDF.js - using jsdelivr CDN (more reliable)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// CMap 配置 - 用于正确加载中文等 CJK 字体
const CMAP_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/cmaps/`;
const CMAP_PACKED = true;

/**
 * Convert PDF file to image (first page only)
 * @param file - PDF file
 * @returns Image file (PNG format)
 */
export async function pdfToImage(file: File): Promise<File> {
  try {
    console.log('[PDF转换] 开始转换 PDF 到图片...');

    // Read PDF file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const pdf = await pdfjsLib.getDocument({
      data: arrayBuffer,
      cMapUrl: CMAP_URL,
      cMapPacked: CMAP_PACKED,
    }).promise;
    console.log(`[PDF转换] PDF 加载成功，共 ${pdf.numPages} 页`);

    // Get first page
    const page = await pdf.getPage(1);
    console.log('[PDF转换] 获取第一页成功');

    // 获取页面信息用于调试
    const pageInfo = {
      pageNumber: 1,
      rotation: page.rotate,
      view: page.view,
      userUnit: page.userUnit
    };
    console.log('[PDF转换] 页面信息:', pageInfo);

    // Set scale for better quality - 提高到3倍以获得更好的OCR效果
    const scale = 3.0; // Higher scale = better quality for OCR
    const viewport = page.getViewport({ scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('无法创建 Canvas 上下文');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // 先用白色填充背景，确保透明图层有底色
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Render PDF page to canvas with all layers
    const renderContext = {
      canvasContext: context,
      viewport: viewport,
      // 启用注释层和表单层的渲染
      intent: 'display', // 'display' 会渲染所有可见内容
      enableWebGL: false,
      renderInteractiveForms: true, // 渲染表单字段
      annotationMode: 2 // AnnotationMode.ENABLE_FORMS - 启用注释和表单
    };

    await page.render(renderContext).promise;
    console.log('[PDF转换] 页面渲染完成（包含所有图层）');

    // 在开发环境中，显示转换后的图片以便调试
    if (import.meta.env.DEV) {
      const dataUrl = canvas.toDataURL('image/png');
      console.log('[PDF转换] 转换后的图片预览（右键复制图片地址在新标签页打开）:');
      console.log('%c ', `
        font-size: 200px;
        background: url(${dataUrl}) no-repeat;
        background-size: contain;
      `);
      console.log('图片尺寸:', canvas.width, 'x', canvas.height);
    }

    // Convert canvas to Blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas 转换失败'));
        }
      }, 'image/png', 0.95);
    });

    // Convert Blob to File
    const imageFile = new File(
      [blob],
      file.name.replace('.pdf', '.png'),
      { type: 'image/png' }
    );

    console.log('[PDF转换] 转换成功！');
    console.log(`[PDF转换] 原文件大小: ${(file.size / 1024).toFixed(2)} KB`);
    console.log(`[PDF转换] 图片大小: ${(imageFile.size / 1024).toFixed(2)} KB`);

    return imageFile;

  } catch (error) {
    console.error('[PDF转换] 转换失败:', error);
    throw error;
  }
}

/**
 * Check if file is PDF
 * @param file - File to check
 * @returns true if PDF
 */
export function isPDF(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Extract text from PDF file
 * @param file - PDF file
 * @returns Extracted text content
 */
export async function extractTextFromPDF(file: File): Promise<string> {
  try {
    console.log('[PDF文本提取] 开始提取文本...');

    // Read PDF file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const pdf = await pdfjsLib.getDocument({
      data: arrayBuffer,
      cMapUrl: CMAP_URL,
      cMapPacked: CMAP_PACKED,
    }).promise;
    console.log(`[PDF文本提取] PDF 加载成功，共 ${pdf.numPages} 页`);

    let fullText = '';

    // Extract text from all pages
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Combine all text items
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');

      fullText += pageText + '\n';
    }

    console.log('[PDF文本提取] 提取成功！');
    console.log('[PDF文本提取] 文本长度:', fullText.length);
    console.log('[PDF文本提取] 文本预览:', fullText.substring(0, 200));

    return fullText;

  } catch (error) {
    console.error('[PDF文本提取] 提取失败:', error);
    throw error;
  }
}
